import React, { useCallback, useMemo } from 'react';
import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import { testId } from 'ui-components/services/testid-service';

import { allPackageTypes, CUSTOM_PACKAGE_GROUP_ID, CUSTOM_PACKAGE_SET_GROUP_ID } from './package-group-resolver';
import style from './style.module.css';
import { useMultiPackageShipments } from '../../../feature-flags/hooks/use-multi-package-shipments';
import { CUSTOM_PACKAGES_TAB, CUSTOM_PACKAGE_SETS_TAB } from '../../../pages/settings/packages-settings';
import { useFulfillmentPlannerContext } from '../../context';
import { messages } from '../../messages';

import smartFillStyle from 'ui-components/components/smartfill.module.less';

const getPackageName = (packageGroups, selectedPackageId, placeholder) => {
  if (selectedPackageId === allPackageTypes.id) {
    return <FormattedMessage {...messages.allPackageTypes} />;
  }

  if (
    selectedPackageId === undefined ||
    selectedPackageId === '' ||
    (typeof selectedPackageId === 'string' && selectedPackageId.startsWith('null'))
  ) {
    return placeholder;
  }

  // Combine all packages from each package group into a single array
  const allPackages = packageGroups.flatMap((group) => (group && group.packages) || []);

  // Find the package matching the selectedPackageId
  const selectedPackage = allPackages.find(
    (pkg) => pkg.id === selectedPackageId || (pkg.packageSetId && pkg.packageSetId === selectedPackageId),
  );

  return selectedPackage?.name;
};

// TODO: this component has leaked outside of the fulfillment planner, clean that up
/**
 * @param {object} props
 * @param {string} [props.id]
 * @param {string} [props.dropdownMenuType]
 * @param {string} [props.className]
 * @param {(value: string, dimensions: object) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {Array} props.packageGroups
 * @param {Array} props.selectedPackageIds
 * @param {string} [props.serviceCollectionGuid]
 * @param {(packageSetId: string) => void} props.onChangePackageSet
 * @returns {React.ReactNode}
 */
export const PackageDropdown = ({
  id,
  dropdownMenuType,
  className,
  onChange = noop,
  disabled = false,
  packageGroups = [],
  selectedPackageIds = [],
  serviceCollectionGuid,
  onChangePackageSet,
}) => {
  const intl = useIntl();
  const { setHasSmartFillDataBeenChanged, hasSmartFillDataBeenChanged, isSmartFillEnabled } = useFulfillmentPlannerContext();
  const enableMultiPackageShipments = useMultiPackageShipments();
  const multiPackageCount = selectedPackageIds.length;
  const hasMultiplePackages = enableMultiPackageShipments && multiPackageCount > 1;

  const tooltipMessage = useMemo(
    () => (disabled && serviceCollectionGuid && !hasMultiplePackages ? <FormattedMessage {...messages.packageNotSupported} /> : undefined),
    [disabled, serviceCollectionGuid, hasMultiplePackages],
  );

  const multiPackageText = useMemo(
    () =>
      enableMultiPackageShipments
        ? intl.formatMessage(messages.multiPackageCount, {
            count: multiPackageCount,
          })
        : intl.formatMessage(messages.multiplePackages),
    [enableMultiPackageShipments, multiPackageCount, intl],
  );

  const getSelectedPackage = useCallback(
    (packageType) => {
      if (packageType.packageSetId) {
        return selectedPackageIds[0] === packageType.packageSetId;
      }
      return selectedPackageIds[0] === packageType.id;
    },
    [selectedPackageIds],
  );

  const onChangeHandler = useCallback(
    ({ packageId, length, width, height, unit, packageGroupId, packageSetId }) => {
      // If the selected package is from a package set, apply the package set
      if (enableMultiPackageShipments && packageGroupId === CUSTOM_PACKAGE_SET_GROUP_ID) {
        onChangePackageSet(packageSetId);
      } else {
        // Handle regular package selection
        const dimensions = {
          length: Number.parseFloat(length),
          width: Number.parseFloat(width),
          height: Number.parseFloat(height),
          unit,
        };
        onChange(packageId, dimensions);
        setHasSmartFillDataBeenChanged((prevState) => ({
          ...prevState,
          package: true,
          height: true,
          length: true,
          width: true,
        }));
      }
    },
    [enableMultiPackageShipments, onChange, onChangePackageSet, setHasSmartFillDataBeenChanged],
  );

  const hasSmartFillPackageChanged = hasSmartFillDataBeenChanged?.package;
  const shouldShowSmartFillIcon = isSmartFillEnabled && !hasSmartFillPackageChanged;

  const navigate = useNavigate();

  const navigateToPackagesTab = useCallback(
    (packageGroupId) => {
      if (packageGroupId === CUSTOM_PACKAGE_GROUP_ID) {
        navigate(`/settings/packages?tab=${CUSTOM_PACKAGES_TAB}`);
      }
      if (packageGroupId === CUSTOM_PACKAGE_SET_GROUP_ID) {
        navigate(`/settings/packages?tab=${CUSTOM_PACKAGE_SETS_TAB}`);
      }
    },
    [navigate],
  );

  const packageTypeWithLink = (name, packageGroupId) => (
    <FlexRow className={classnames(style['package-type-with-link'])}>
      <Text textType={TextType.Label} value={name} />
      <Button onClick={() => navigateToPackagesTab(packageGroupId)} theme='link'>
        <FormattedMessage {...messages.managePackagesLink} />
      </Button>
    </FlexRow>
  );

  const dropdownMenuItemGroupLabel = (name, packageGroupId) => {
    const isCustomPackageSetGroup = [CUSTOM_PACKAGE_GROUP_ID, CUSTOM_PACKAGE_SET_GROUP_ID].includes(packageGroupId);

    return isCustomPackageSetGroup && enableMultiPackageShipments
      ? packageTypeWithLink(name, packageGroupId)
      : intl.formatMessage(name, name?.values);
  };

  // noinspection JSValidateTypes
  return (
    <FormattedMessage {...messages.placeholder}>
      {(placeholderText) => {
        const text = multiPackageCount > 1 ? multiPackageText : getPackageName(packageGroups, selectedPackageIds[0], placeholderText);
        return (
          <DropdownMenu
            {...testId('csw-package')}
            ariaLabel={intl.formatMessage(messages.package)}
            className={classnames(className, {
              [smartFillStyle['smart-fill-container']]: shouldShowSmartFillIcon,
            })}
            disabled={disabled}
            disabledTooltipMessage={tooltipMessage}
            dropdownMenuShouldHaveWidthOfButton
            dropdownMenuType={dropdownMenuType}
            fullWidth
            id={id}
            placeholder={placeholderText}
            value={selectedPackageIds[0]}
            variant='form'
          >
            <DropdownMenuToggler
              className={classnames(style['csw-package-dropdown-toggler'], {
                [style['smart-fill']]: shouldShowSmartFillIcon,
              })}
            >
              {text || <FormattedMessage {...messages.placeholder} />}
            </DropdownMenuToggler>
            {packageGroups.length === 0 ? (
              <DropdownMenuItem disabled>
                <FormattedMessage {...messages.noPackagesAvailable} />
              </DropdownMenuItem>
            ) : (
              packageGroups.map(({ id: packageGroupId, name, dimensions, packages }) => {
                if (!packages) {
                  return (
                    <DropdownMenuItem
                      key={packageGroupId}
                      onClick={() =>
                        onChangeHandler({
                          packageId: packageGroupId,
                          length: dimensions?.length,
                          width: dimensions?.width,
                          height: dimensions?.height,
                          unit: dimensions?.unit,
                          packageGroupId,
                          packageSetId: undefined,
                        })
                      }
                      selected={selectedPackageIds[0] === packageGroupId}
                    >
                      {name}
                    </DropdownMenuItem>
                  );
                }
                return (
                  <DropdownMenuItemGroup key={packageGroupId} label={dropdownMenuItemGroupLabel(name, packageGroupId)}>
                    {packages.map((packageType) => (
                      <DropdownMenuItem
                        key={packageType.id || packageType.packageSetId}
                        onClick={() =>
                          onChangeHandler({
                            packageId: packageType.id,
                            length: packageType.dimensions?.length,
                            width: packageType.dimensions?.width,
                            height: packageType.dimensions?.height,
                            unit: packageType.dimensions?.unit,
                            packageGroupId,
                            packageSetId: packageType?.packageSetId,
                          })
                        }
                        selected={getSelectedPackage(packageType)}
                      >
                        {packageType.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuItemGroup>
                );
              })
            )}
          </DropdownMenu>
        );
      }}
    </FormattedMessage>
  );
};
